import {
  ComponentPropsWithRef,
  ForwardedRef,
  useEffect,
  ChangeEvent,
  useRef,
  useState,
  forwardRef,
} from "react";
import { t } from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faUpload } from "@fortawesome/pro-duotone-svg-icons";
import { VariantProps, tv } from "tailwind-variants";

import { Button } from "@/components/Button";
import { P } from "@/components/Typography";

const styles = tv({
  slots: {
    base: "flex flex-col gap-10",
    inputContainer:
      "flex h-60 w-full flex-row items-center justify-between gap-x-5 rounded border border-solid border-black  px-10 py-20 hover:cursor-pointer sm:max-w-[384px] sm:gap-x-50",
    labelContainer: "min-w-[170px]  sm:min-w-[230px]",
    label: "text-base",
    helperTextContainer: "flex flex-row items-center gap-10 !text-caption",
    dragContainer:
      "absolute bottom-0 left-0 right-0 top-0 h-full w-full rounded-lg",
  },
  variants: {
    variant: {
      error: {
        inputContainer: "border-red",
        label: "text-red",
        helperTextContainer: "text-red",
      },
      success: {
        label: "text-primary",
      },
    },
    fullWidth: {
      true: {
        base: "flex-grow",
        inputContainer: "h-full min-w-full max-w-full flex-grow",
      },
    },
  },
});

interface IFileInputProps extends ComponentPropsWithRef<"input"> {
  reset: boolean;
  helperText?: string;
  variant?: VariantProps<typeof styles>["variant"];
  handleFileUpload: (file?: File) => void;
  fullWidth?: boolean;
}

const FileInput = forwardRef(
  (
    {
      handleFileUpload,
      reset,
      helperText,
      variant,
      fullWidth = false,
      ...rest
    }: IFileInputProps,
    ref: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const { name, onChange } = rest;

    const {
      base,
      inputContainer,
      labelContainer,
      label,
      helperTextContainer,
      dragContainer,
    } = styles({
      variant,
      fullWidth,
    });

    const [dragActive, setDragActive] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setRefs = (element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current =
          element;
      }
    };

    const getDisplayFileName = (name?: string) =>
      name && name.length > 27 ? `${name.substring(0, 25)}...` : name;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e);
      const file = e?.target?.files?.[0];
      setFileName(getDisplayFileName(file?.name));
      handleFileUpload(file);
    };

    const handleOpenFileWindow = () => {
      inputRef.current?.click();
    };

    const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e?.dataTransfer?.files?.[0];
      if (file) {
        setFileName(getDisplayFileName(file.name));
        handleFileUpload(file);
      }
    };

    useEffect(() => {
      if (inputRef.current && reset) {
        inputRef.current.value = "";
        inputRef.current.type = "text";
        inputRef.current.type = "file";
        setFileName(undefined);
      }
    }, [reset]);

    const renderFileName = () => (
      <span className={label()}>
        {fileName ?? t("components.fileInput.placeholder")}
      </span>
    );

    const renderHelperText = () =>
      helperText && (
        <P className={helperTextContainer()}>
          <FontAwesomeIcon className="mx-2" icon={faInfoCircle} />
          {helperText}
        </P>
      );

    return (
      <div className={base()} onDragEnter={handleDrag}>
        <div className={inputContainer()}>
          <input
            id={name ?? "test-input"}
            type="file"
            className="hidden"
            {...rest}
            onChange={handleChange}
            ref={setRefs}
          />
          <div className={labelContainer()} onClick={handleOpenFileWindow}>
            {renderFileName()}
          </div>
          <Button
            type="button"
            variant="link-light"
            trackingId="FileInput"
            width={"unset"}
            icon={faUpload}
            iconPosition="end"
            iconClassName="h-15"
            onClick={handleOpenFileWindow}>
            {t("components.fileInput.cta")}
          </Button>
        </div>
        {renderHelperText()}
        {dragActive && (
          <div
            className={dragContainer()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        )}
      </div>
    );
  },
);

export default FileInput;
